import './style.css';
import { calendarYearDateKeys, cupStateForDisplay, cupVisualMeta, drinkTransition, historyHeatLevel,
  historySummary, hydrationProgressState, normalisePairingCode, recentDateKeys, resolveTheme,
  shiftHeatmapYear,
  type DrinkTransition, type HistoryDay, type HydrationProgressState, type HydrationSnapshot,
  type RecentHydrationEvent, type Theme } from './ui';

declare global {
  interface Window {
    liff?: {
      init(input: { liffId: string }): Promise<void>;
      isLoggedIn(): boolean;
      login(input?: { redirectUri?: string }): void;
      getIDToken(): string | null;
      isInClient(): boolean;
      closeWindow(): void;
      logout(): void;
    };
  }
}

type View = 'today' | 'history' | 'settings';
interface ReminderCommand {
  id: string;
  revision: number;
  type: string;
  status: string;
  createdAt: string;
  ackedAt?: string | null;
}
interface TodayPayload {
  ok: boolean;
  bound: boolean;
  sequence?: number;
  lastSeenAt?: string | null;
  status?: Record<string, unknown>;
  today?: { total_ml: number; drink_count: number; last_drink_at: string | null };
  recentEvent?: (RecentHydrationEvent & { seq: number; amountMl?: number | null }) | null;
  reminderCommands?: ReminderCommand[];
}
interface SettingsPayload {
  bound: boolean;
  pending: boolean;
  syncStatus?: string;
  settings?: Record<string, unknown>;
  command?: { id: string; revision: number; status: string } | null;
}
interface HistoryPayload {
  bound: boolean;
  days: HistoryDay[];
}

const root = document.querySelector<HTMLDivElement>('#app')!;
const themeMedia = matchMedia('(prefers-color-scheme: dark)');
const previewMode = import.meta.env.DEV && new URLSearchParams(location.search).has('preview');
let activeView: View = 'today';
let lastSequence = -1;
let pollTimer = 0;
let pendingCommand: { id: string; label: string } | null = null;
let commandInFlight = false;
let commandFeedback = '';
let settingsCommandId: string | null = null;
let settingsInFlight = false;
let lastTodaySnapshot: HydrationSnapshot | null = null;
let gaugeFeedbackTimer = 0;
let drinkAnimationTimer = 0;
let viewGeneration = 0;
let todayRequestGeneration = 0;
let settingsRefreshGeneration = 0;
let heatmapYear: number | null = null;
let heatmapFetchSequence = 0;
let heatmapTodayKey = '';
let heatmapGoal = 2000;
const heatmapCache = new Map<string, HistoryPayload>();

function syncPageVisibility() {
  document.documentElement.classList.toggle('page-hidden', document.hidden);
}

document.addEventListener('visibilitychange', syncPageVisibility);
syncPageVisibility();

function icon(name: View | 'theme') {
  const paths = {
    today: '<path d="M5 14c0-4 3-6 7-10 4 4 7 6 7 10a7 7 0 0 1-14 0Z"/><path d="M9 15c.7 1.3 1.7 2 3 2"/>',
    history: '<path d="M4 18V9m6 9V5m6 13v-7m4 7H2"/>',
    settings: '<path d="M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4Z"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3A1.7 1.7 0 0 0 14 21v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14h-.2v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3A1.7 1.7 0 0 0 10 3v-.2h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z"/>',
    theme: '<path d="M12 3a9 9 0 1 0 9 9c-5 2-10-3-9-9Z"/>',
  };
  return `<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">${paths[name]}</svg>`;
}

function brandMark() {
  return `<svg class="brand-symbol" viewBox="0 0 48 48" aria-hidden="true">
    <path class="brand-cup" d="M14 12h17l-1.5 22H15.5L14 12Z"/>
    <path class="brand-water" d="M17 24c4-3 7 3 11 0l-.5 7h-10Z"/>
    <path class="brand-drop" d="M34 10c0 3-1.7 5-4 5s-4-2-4-5 4-7 4-7 4 4 4 7Z"/>
    <path class="brand-ripple" d="M8 39c8 3 24 3 32 0M12 43c7 2 17 2 24 0"/>
  </svg>`;
}

function currentTheme(): Theme {
  return resolveTheme(localStorage.getItem('hydracup-theme'), themeMedia.matches);
}

function applyTheme(theme: Theme, persist = false) {
  document.documentElement.dataset.theme = theme;
  if (persist) localStorage.setItem('hydracup-theme', theme);
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#0b1c1d' : '#edf7f4');
  root.querySelector<HTMLButtonElement>('[data-theme-toggle]')?.setAttribute('aria-label', theme === 'dark' ? '切換為亮色主題' : '切換為深色主題');
}

function shell(content: string, options: { dock?: boolean } = {}) {
  root.innerHTML = `
    <div class="hydro-lines" aria-hidden="true"><i></i><i></i><i></i></div>
    <header class="masthead">
      <a class="wordmark" href="#today" aria-label="HydraCup 今日飲水">
        ${brandMark()}<span><b>HydraCup</b><small>HYDRATION COMPANION</small></span>
      </a>
      <div class="mast-actions"><span class="live-mark"><i></i> LIVE</span>
        <button class="icon-button" type="button" data-theme-toggle>${icon('theme')}</button></div>
    </header>
    <main>${content}</main>
    ${options.dock === false ? '' : `<nav class="dock" aria-label="主要導覽">
      ${navButton('today', '今日')}${navButton('history', '趨勢')}${navButton('settings', '設定')}
    </nav>`}`;
  root.querySelectorAll<HTMLButtonElement>('[data-view]').forEach((button) => {
    button.addEventListener('click', () => navigate(button.dataset.view as View));
  });
  root.querySelector<HTMLAnchorElement>('.wordmark')?.addEventListener('click', (event) => {
    event.preventDefault();
    void navigate('today');
  });
  root.querySelector<HTMLButtonElement>('[data-theme-toggle]')?.addEventListener('click', () => {
    applyTheme(document.documentElement.dataset.theme === 'dark' ? 'light' : 'dark', true);
  });
  applyTheme(currentTheme());
}

function navButton(view: View, label: string) {
  return `<button data-view="${view}" class="${activeView === view ? 'active' : ''}"${activeView === view ? ' aria-current="page"' : ''}>
    ${icon(view)}<span>${label}</span></button>`;
}

function escapeHtml(value: unknown) {
  return String(value ?? '').replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[char]!);
}

function previewPayload(path: string): unknown {
  if (path.startsWith('/today')) return {
    ok: true, bound: true, sequence: 42, lastSeenAt: new Date().toISOString(),
    status: { todayTotalMl: 1360, dailyGoalMl: 2000, drinkCount: 7, lastDrinkAt: new Date(Date.now() - 18 * 60000).toISOString(), cupState: 'CUP_STABLE', reminderState: 'counting', reminderRemainingSec: 1860 }
  };
  if (path.startsWith('/history')) {
    const todayKey = new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Taipei' });
    const yearParam = new URL(path, 'https://preview.hydracup.local').searchParams.get('year');
    const keys = yearParam === null ? recentDateKeys(todayKey, 365) : calendarYearDateKeys(Number(yearParam));
    return {
      bound: true,
      days: keys
      .map((local_date, index) => {
        const cycle = index % 14;
        const total_ml = cycle === 0 || cycle === 9 ? 0 : 760 + (index * 173 % 1660);
        return { local_date, total_ml, drink_count: total_ml ? 3 + (index % 8) : 0 };
      }).filter((day) => day.total_ml > 0)
    };
  }
  if (path.startsWith('/settings')) return { bound: true, pending: false, syncStatus: 'synced', settings: { daily_goal_ml: 2000, reminder_interval_min: 60, reminder_enabled: 1 } };
  return { ok: true, pending: true };
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  if (previewMode) return previewPayload(path) as T;
  const response = await fetch(`/api/v1${path}`, {
    credentials: 'same-origin',
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) }
  });
  if (response.status === 401) throw new Error('AUTH');
  const data = await response.json() as T & { error?: string };
  if (!response.ok) throw new Error(data.error ?? `HTTP_${response.status}`);
  return data;
}

async function authenticate() {
  const liffId = import.meta.env.VITE_LIFF_ID as string | undefined;
  if (!liffId || !window.liff) {
    shell(`<section class="state-page"><p class="eyebrow">SETUP REQUIRED</p>
      <h1>還差一個 LIFF ID</h1><p>請在部署環境設定 <code>VITE_LIFF_ID</code>，再重新建置。</p></section>`);
    return false;
  }
  await window.liff.init({ liffId });
  if (!window.liff.isLoggedIn()) {
    window.liff.login({ redirectUri: location.href.split('#')[0] });
    return false;
  }
  const idToken = window.liff.getIDToken();
  if (!idToken) throw new Error('LINE_ID_TOKEN_MISSING');
  await fetch('/api/v1/auth/line', {
    method: 'POST', credentials: 'same-origin',
    headers: { 'content-type': 'application/json' }, body: JSON.stringify({ idToken })
  }).then((response) => { if (!response.ok) throw new Error('LINE_AUTH_FAILED'); });
  return true;
}

async function ensureSession() {
  try {
    await api<TodayPayload>('/today');
    return true;
  } catch (error) {
    if ((error as Error).message !== 'AUTH') throw error;
    return authenticate();
  }
}

function formatTime(value: unknown) {
  if (!value) return '尚無紀錄';
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? '尚未同步時間' : date.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' });
}

function reminderCopy(state: unknown, remaining: unknown) {
  const stateName = String(state);
  const pausedCopy = ({ waiting_for_cup: '放上杯子後開始', paused_cup_away: '杯子離開，時間暫停',
    paused_today: '今天已暫停', disabled: '提醒已關閉' } as Record<string, string>)[stateName];
  if (pausedCopy) return pausedCopy;
  const seconds = Number(remaining) || 0;
  if (seconds > 0) return `${Math.ceil(seconds / 60)} 分鐘後`;
  return ({ alerted: '現在，喝一口水吧', snoozed: '稍後提醒' } as Record<string, string>)[stateName] ?? '計時中';
}

function cupScene(status: Record<string, unknown>, announce = false) {
  const meta = cupVisualMeta(String(status.cupState ?? 'no_cup'), String(status.reminderState ?? ''));
  return `<section class="cup-status ${meta.className}" role="status" aria-live="${announce ? 'polite' : 'off'}" aria-label="${escapeHtml(meta.label)}：${escapeHtml(meta.detail)}">
    <div class="cup-illustration" aria-hidden="true">
      <svg viewBox="0 0 160 150"><path class="cup-handle" d="M111 49h12c24 0 24 41-2 41h-7"/>
        <path class="cup-body" d="M39 36h75l-7 87c-1 10-8 15-17 15H61c-9 0-16-5-17-15Z"/>
        <path class="cup-water" d="M46 76c18-8 38 8 62-1l-4 45c-.7 7-5 11-12 11H60c-7 0-11-4-12-11Z"/>
        <path class="cup-rim" d="M35 36h83"/><path class="cup-drop" d="M80 58c0 7-4 11-9 11s-9-4-9-11 9-17 9-17 9 10 9 17Z"/></svg>
      <i class="ripple ripple-one"></i><i class="ripple ripple-two"></i>
    </div>
    <div><span class="status-kicker">CUP &amp; REMINDER</span><strong>${escapeHtml(meta.label)}</strong><p>${escapeHtml(meta.detail)}</p></div>
  </section>`;
}

function accountControls(bound: boolean) {
  return `<details class="account-panel">
    <summary><span><small>ACCOUNT &amp; DATA</small>帳號與資料</span><i>展開</i></summary>
    <div class="account-content">
    ${bound ? '<p>解除綁定會保留雲端飲水紀錄。</p><button class="unlink" id="unlink">解除這台 HydraCup</button>' : ''}
    <p>登出會撤銷所有 HydraCup WebUI session。</p>
    <button class="unlink" id="logout">登出所有 WebUI</button>
    <hr>
    <p>刪除帳號會永久刪除飲水歷史、設定、通知與 LINE 綁定，並停用這台杯子的雲端同步。杯子仍可離線使用，但不能自行重新配對。</p>
    <button class="delete-account" id="delete-account">永久刪除帳號與飲水資料</button>
    </div></details>`;
}

function leaveAuthenticatedApp() {
  if (window.liff?.isInClient()) window.liff.closeWindow();
  else {
    window.liff?.logout();
    location.reload();
  }
}

function bindAccountControls(bound: boolean) {
  if (bound) {
    document.querySelector<HTMLButtonElement>('#unlink')!.addEventListener('click', async () => {
      if (!confirm('確定解除這台 HydraCup？裝置上的飲水紀錄不會被刪除。')) return;
      await api('/binding', { method: 'DELETE' });
      pairing();
    });
  }
  document.querySelector<HTMLButtonElement>('#logout')!.addEventListener('click', async () => {
    if (!confirm('確定撤銷所有 HydraCup WebUI session？')) return;
    await api('/session', { method: 'DELETE' });
    leaveAuthenticatedApp();
  });
  document.querySelector<HTMLButtonElement>('#delete-account')!.addEventListener('click', async (event) => {
    const confirmation = prompt('此操作無法復原。請輸入 DELETE 以永久刪除帳號與飲水資料。');
    if (confirmation !== 'DELETE') return;
    const button = event.currentTarget as HTMLButtonElement;
    button.disabled = true;
    try {
      await api('/account', { method: 'DELETE', body: JSON.stringify({ confirmation }) });
      leaveAuthenticatedApp();
    } catch (error) {
      button.disabled = false;
      alert(`刪除失敗：${(error as Error).message}`);
    }
  });
}

function pairing() {
  shell(`<section class="pairing-layout"><div class="pairing-visual">${cupScene({ cupState: 'no_cup' })}</div>
    <div class="pairing-copy"><p class="eyebrow">ONE CUP · ONE ACCOUNT</p>
      <h1>把你的杯子<br>帶進日常。</h1>
      <p>到 HydraCup 本機 Device Console 找到 8 碼配對碼。配對碼會在 10 分鐘後失效。</p>
      <form id="pair-form"><label for="pair-code">8 碼配對碼</label>
        <input id="pair-code" minlength="8" maxlength="11" autocomplete="one-time-code" autocapitalize="characters" spellcheck="false" placeholder="WATER8UP" />
        <button class="primary" type="submit">連結 HydraCup</button><output id="pair-message" aria-live="polite"></output></form>
      ${accountControls(false)}</div></section>`, { dock: false });
  document.querySelector<HTMLFormElement>('#pair-form')!.addEventListener('submit', async (event) => {
    event.preventDefault();
    const message = document.querySelector<HTMLOutputElement>('#pair-message')!;
    const input = document.querySelector<HTMLInputElement>('#pair-code')!;
    const code = normalisePairingCode(input.value);
    input.value = code;
    if (!/^[A-Z0-9]{8}$/.test(code)) {
      message.textContent = '請輸入本機畫面上的 8 碼英數配對碼。';
      input.focus();
      return;
    }
    message.textContent = '正在確認配對碼…';
    try {
      await api('/pairing/claim', { method: 'POST', body: JSON.stringify({ code }) });
      await renderToday();
    } catch (error) { message.textContent = `無法配對：${(error as Error).message}`; }
  });
  bindAccountControls(false);
}

function reminderCommandLabel(type: string) {
  return type === 'pause_today' ? '今日暫停' : '稍後提醒';
}

function setReminderActionsDisabled(disabled: boolean) {
  root.querySelectorAll<HTMLButtonElement>('[data-snooze], [data-pause]').forEach((button) => {
    button.disabled = disabled;
    if (!disabled) button.removeAttribute('aria-busy');
  });
}

const progressBandClasses = [
  'band-empty', 'band-starting', 'band-building', 'band-steady', 'band-near',
  'band-goal', 'band-overflow', 'band-overflow-strong', 'band-overflow-capped'
];

function mountTodayView() {
  shell(`<section class="today-hero">
      <div class="hero-copy"><p class="eyebrow">${new Date().toLocaleDateString('zh-TW', { timeZone: 'Asia/Taipei', month: 'long', day: 'numeric', weekday: 'long' })}</p>
      <h1>今天，<br>慢慢喝。</h1><p>每一次拿起與放回，都讓身體的節奏更清楚。</p></div>
      <div class="gauge-stage band-empty" id="gauge-stage" style="--level:0%">
        <button class="water-gauge" id="water-gauge" type="button" data-summary="今天從第一口開始" aria-label="今日已喝 0 ml，達成 0%，按下查看摘要">
          <span class="water"><i></i><i></i></span>
          <span class="gauge-bubbles" aria-hidden="true"><i></i><i></i><i></i></span>
          <span class="gauge-event" aria-hidden="true"><i class="event-drop"></i><i class="event-drop"></i><i class="event-drop"></i><i class="event-splash"></i><i class="event-ripple"></i></span>
          <span class="gauge-copy"><strong id="today-total">0</strong><span>ml</span><small id="today-percent">0%</small></span>
        </button>
        <span class="gauge-overflow" aria-hidden="true"><i></i><i></i><b></b></span>
        <output class="drink-gain" id="drink-gain" aria-live="polite"></output>
      </div>
      <p class="goal-line"><span id="today-goal">目標 -- ml</span><i></i><span id="today-count">0 次飲水</span></p>
      <output class="gauge-feedback" id="gauge-feedback" aria-live="polite"></output>
    </section>
    <div id="today-cup-slot"></div>
    <section class="rhythm-card">
      <div><span>上次喝水</span><strong id="today-last-drink">尚無紀錄</strong></div>
      <div><span>下一次提醒</span><strong id="today-next-reminder">計時中</strong></div>
      <p class="sync-note" id="today-sync-note">等待裝置第一次同步</p>
    </section>
    <section class="soft-actions"><span class="section-label">REMINDER ACTIONS</span><div><button data-snooze="20">20 分鐘後</button><button data-snooze="40">40 分鐘後</button><button data-snooze="60">60 分鐘後</button><button data-pause>今天暫停</button></div>
      <output id="command-message" aria-live="polite"></output></section>`);
  bindTodayInteractions();
}

function bindTodayInteractions() {
  const gauge = root.querySelector<HTMLButtonElement>('#water-gauge')!;
  gauge.addEventListener('click', (event) => {
    const rect = gauge.getBoundingClientRect();
    const direction = event.detail === 0 ? 'center' : event.clientX < rect.left + rect.width / 2 ? 'left' : 'right';
    gauge.classList.remove('tap-left', 'tap-center', 'tap-right');
    requestAnimationFrame(() => gauge.classList.add(`tap-${direction}`));
    const feedback = root.querySelector<HTMLOutputElement>('#gauge-feedback')!;
    feedback.textContent = gauge.dataset.summary ?? '';
    feedback.classList.add('visible');
    window.clearTimeout(gaugeFeedbackTimer);
    gaugeFeedbackTimer = window.setTimeout(() => feedback.classList.remove('visible'), 2800);
  });
  gauge.addEventListener('animationend', () => gauge.classList.remove('tap-left', 'tap-center', 'tap-right'));
  root.querySelectorAll<HTMLButtonElement>('[data-snooze]').forEach((button) => button.addEventListener('click', async () => {
    await queueReminderCommand(button, '/reminders/snooze', { minutes: Number(button.dataset.snooze) }, '稍後提醒');
  }));
  root.querySelector<HTMLButtonElement>('[data-pause]')!.addEventListener('click', async (event) => {
    await queueReminderCommand(event.currentTarget as HTMLButtonElement, '/reminders/pause-today', {}, '今日暫停');
  });
}

function playDrinkAnimation(transition: DrinkTransition) {
  if (document.hidden) return;
  const stage = root.querySelector<HTMLElement>('#gauge-stage');
  const gain = root.querySelector<HTMLOutputElement>('#drink-gain');
  if (!stage || !gain) return;
  stage.dataset.drops = transition.amountMl <= 120 ? '1' : transition.amountMl <= 280 ? '2' : '3';
  gain.textContent = `+${transition.amountMl} ml`;
  stage.classList.remove('is-drink-event', 'is-goal-crossing');
  void stage.offsetWidth;
  stage.classList.add('is-drink-event');
  if (transition.crossedGoal) stage.classList.add('is-goal-crossing');
  window.clearTimeout(drinkAnimationTimer);
  drinkAnimationTimer = window.setTimeout(() => {
    stage.classList.remove('is-drink-event', 'is-goal-crossing');
    gain.textContent = '';
  }, 1900);
}

function updateTodayView(data: TodayPayload, status: Record<string, unknown>, snapshot: HydrationSnapshot,
                         progress: HydrationProgressState, displayStatus: Record<string, unknown>,
                         sequenceChanged: boolean, transition: DrinkTransition | null) {
  const stage = root.querySelector<HTMLElement>('#gauge-stage')!;
  stage.classList.remove(...progressBandClasses);
  stage.classList.add(`band-${progress.band}`);
  stage.style.setProperty('--level', `${progress.visualPercent}%`);
  const gauge = root.querySelector<HTMLButtonElement>('#water-gauge')!;
  gauge.dataset.summary = progress.summary;
  gauge.setAttribute('aria-label', `今日已喝 ${snapshot.totalMl} ml，達成 ${progress.percent}%，按下查看摘要`);
  root.querySelector('#today-total')!.textContent = String(snapshot.totalMl);
  root.querySelector('#today-percent')!.textContent = `${progress.percent}%`;
  root.querySelector('#today-goal')!.textContent = `目標 ${Math.max(1, Math.round(Number(status.dailyGoalMl) || 2000))} ml`;
  root.querySelector('#today-count')!.textContent = `${snapshot.drinkCount} 次飲水`;
  root.querySelector('#today-last-drink')!.textContent = formatTime(status.lastDrinkAt ?? data.today?.last_drink_at);
  root.querySelector('#today-next-reminder')!.textContent = reminderCopy(status.reminderState, status.reminderRemainingSec);
  root.querySelector('#today-sync-note')!.textContent = data.lastSeenAt ? `裝置於 ${formatTime(data.lastSeenAt)} 同步` : '等待裝置第一次同步';
  root.querySelector('#today-cup-slot')!.innerHTML = cupScene(displayStatus, sequenceChanged);
  root.querySelector('#command-message')!.textContent = commandFeedback;
  setReminderActionsDisabled(Boolean(pendingCommand || commandInFlight));
  if (transition) playDrinkAnimation(transition);
}

async function renderToday(generation = viewGeneration) {
  const requestGeneration = ++todayRequestGeneration;
  const data = await api<TodayPayload>('/today');
  if (activeView !== 'today' || generation !== viewGeneration || requestGeneration !== todayRequestGeneration) return;
  if (!data.bound) { pairing(); return; }
  const status = data.status ?? {};
  const remoteCommands = data.reminderCommands ?? [];
  const matchingCommand = pendingCommand ? remoteCommands.find((command) => command.id === pendingCommand!.id) : undefined;
  if (pendingCommand && matchingCommand?.status === 'applied') {
    commandFeedback = `${pendingCommand.label}已由杯子確認。`;
    pendingCommand = null;
  } else if (pendingCommand && matchingCommand?.status === 'rejected') {
    commandFeedback = `${pendingCommand.label}遭杯子拒絕，請稍後重試。`;
    pendingCommand = null;
  } else if (pendingCommand && matchingCommand?.status === 'pending') {
    commandFeedback = `${pendingCommand.label}已送出，等待杯子下一次同步確認。`;
  } else if (pendingCommand) {
    const otherPending = remoteCommands.find((command) => command.status === 'pending');
    if (otherPending) {
      pendingCommand = { id: otherPending.id, label: reminderCommandLabel(otherPending.type) };
      commandFeedback = `偵測到另一筆${pendingCommand.label}操作，等待杯子確認。`;
    } else {
      commandFeedback = `${pendingCommand.label}已完成；請以杯子目前狀態為準。`;
      pendingCommand = null;
    }
  } else {
    const remotePending = remoteCommands.find((command) => command.status === 'pending');
    if (remotePending) {
      pendingCommand = { id: remotePending.id, label: reminderCommandLabel(remotePending.type) };
      commandFeedback = `${pendingCommand.label}已送出，等待杯子下一次同步確認。`;
    }
  }
  const total = Math.max(0, Math.round(Number(status.todayTotalMl ?? data.today?.total_ml) || 0));
  const goal = Math.max(1, Math.round(Number(status.dailyGoalMl) || 2000));
  const count = Math.max(0, Math.round(Number(status.drinkCount ?? data.today?.drink_count) || 0));
  const snapshot = { totalMl: total, drinkCount: count };
  const firstMount = !root.querySelector('#gauge-stage');
  const transition = firstMount ? null : drinkTransition(lastTodaySnapshot, snapshot, goal);
  lastTodaySnapshot = snapshot;
  const progress = hydrationProgressState(total, goal);
  const sequenceChanged = lastSequence >= 0 && data.sequence !== lastSequence;
  lastSequence = data.sequence ?? 0;
  const displayStatus = { ...status,
    cupState: cupStateForDisplay(String(status.cupState ?? ''), data.recentEvent) };
  if (firstMount) mountTodayView();
  updateTodayView(data, status, snapshot, progress, displayStatus, sequenceChanged, transition);
}

async function queueReminderCommand(button: HTMLButtonElement, path: string, body: object, label: string) {
  if (commandInFlight || pendingCommand) return;
  const message = document.querySelector<HTMLOutputElement>('#command-message')!;
  commandInFlight = true;
  setReminderActionsDisabled(true);
  button.setAttribute('aria-busy', 'true');
  message.textContent = '正在送出…';
  try {
    const result = await api<{ command?: { id: string } }>(path, { method: 'POST', body: JSON.stringify(body) });
    if (!result.command?.id) throw new Error('COMMAND_ID_MISSING');
    pendingCommand = { id: result.command.id, label };
    commandFeedback = `${label}已送出，等待杯子下一次同步確認。`;
    message.textContent = commandFeedback;
  } catch (error) {
    message.textContent = `操作失敗：${(error as Error).message}`;
    setReminderActionsDisabled(false);
  } finally {
    commandInFlight = false;
  }
}

function dateFromKey(key: string) {
  return new Date(`${key}T00:00:00Z`);
}

function formatVolume(value: number) {
  const rounded = Math.max(0, Math.round(value));
  return rounded >= 10_000 ? `${(rounded / 1000).toFixed(1)} L` : `${rounded} ml`;
}

function fillHistoryDays(rows: HistoryDay[], keys: string[]) {
  const byDate = new Map(rows.map((day) => [day.local_date, day]));
  return keys.map((key) => byDate.get(key) ?? { local_date: key, total_ml: 0, drink_count: 0 });
}

function heatmapMarkup(keys: string[], byDate: Map<string, HistoryDay>, goal: number,
                       periodLabel: string) {
  const firstKey = keys[0];
  if (!firstKey) return '';
  const firstDay = dateFromKey(firstKey).getUTCDay();
  const weeks = Math.ceil((firstDay + keys.length) / 7);
  let previousMonth = '';
  const monthLabels = keys.map((key, index) => {
    const month = key.slice(0, 7);
    if (month === previousMonth) return '';
    previousMonth = month;
    const column = Math.floor((firstDay + index) / 7) + 1;
    const label = `${Number(key.slice(5, 7))}月`;
    return `<span style="grid-column:${column}">${label}</span>`;
  }).join('');
  const blanks = Array.from({ length: firstDay }, () => '<i class="heat-blank" aria-hidden="true"></i>').join('');
  const cells = keys.map((key, index) => {
    const record = byDate.get(key);
    const total = Math.max(0, Number(record?.total_ml) || 0);
    const count = Math.max(0, Math.round(Number(record?.drink_count) || 0));
    const level = historyHeatLevel(total, goal, Boolean(record));
    const dateLabel = dateFromKey(key).toLocaleDateString('zh-TW', { timeZone: 'Asia/Taipei', month: 'long', day: 'numeric', weekday: 'short' });
    const detail = `${dateLabel}：${record ? `${Math.round(total)} ml，共 ${count} 次` : '尚無資料'}`;
    return `<button class="heat-day level-${level}" type="button" data-date="${key}" data-detail="${escapeHtml(detail)}" aria-label="${escapeHtml(detail)}" aria-pressed="false" title="${escapeHtml(detail)}" tabindex="${index === keys.length - 1 ? '0' : '-1'}"></button>`;
  }).join('');
  return `<div class="heatmap-frame" style="--heat-weeks:${weeks}">
    <div class="heatmap-months" style="grid-template-columns:repeat(${weeks},var(--heat-cell))">${monthLabels}</div>
    <div class="heatmap-layout"><div class="heatmap-weekdays" aria-hidden="true"><span>日</span><i></i><span>二</span><i></i><span>四</span><i></i><span>六</span></div>
      <div class="heatmap-grid" role="group" aria-label="${escapeHtml(periodLabel)}每日飲水量">${blanks}${cells}</div></div>
  </div>`;
}

function heatmapPeriodLabel(year: number | null) {
  return year === null ? '最近 365 天' : `${year} 年`;
}

function heatmapCardContent(year: number | null, rows: HistoryDay[]) {
  const keys = year === null ? recentDateKeys(heatmapTodayKey, 365) : calendarYearDateKeys(year);
  const label = heatmapPeriodLabel(year);
  const byDate = new Map(rows.map((day) => [day.local_date, day]));
  const periodDays = fillHistoryDays(rows, keys);
  const stats = historySummary(periodDays, heatmapGoal);
  const currentYear = Number(heatmapTodayKey.slice(0, 4));
  const previous = shiftHeatmapYear(year, -1, currentYear);
  const next = shiftHeatmapYear(year, 1, currentYear);
  const targetLabel = (target: number | null) => target === null ? '最近 365 天' : `${target} 年`;
  const hasData = rows.some((day) => keys.includes(day.local_date));
  return `<div class="history-card-head"><div><span class="section-label">${year === null ? '365 DAY RHYTHM' : `${year} CALENDAR YEAR`}</span><h2>年度飲水熱力圖</h2></div><strong>${formatVolume(stats.totalMl)}</strong></div>
    <div class="heatmap-controls" role="group" aria-label="熱力圖年份切換">
      <button type="button" data-heatmap-direction="-1" aria-label="查看${targetLabel(previous)}">&lt;</button>
      <span class="heatmap-period" aria-live="polite">${label}</span>
      <button type="button" data-heatmap-direction="1" aria-label="查看${targetLabel(next)}">&gt;</button>
    </div>
    <p class="heatmap-intro">每格代表一天，顏色依目前每日目標完成比例加深；空白日期維持淡色。</p>
    ${hasData ? '' : `<p class="heatmap-empty" role="status">${label}尚無飲水紀錄，仍顯示完整日期範圍。</p>`}
    <div class="heatmap-scroll" role="region" aria-label="${label}熱力圖，可水平捲動">${heatmapMarkup(keys, byDate, heatmapGoal, label)}</div>
    <label class="heatmap-scrubber" for="heatmap-scrubber"><span>日期瀏覽</span><input id="heatmap-scrubber" type="range" min="0" max="${keys.length - 1}" value="${keys.length - 1}" step="1"></label>
    <div class="heatmap-foot"><div class="heatmap-legend" aria-label="熱力圖色階"><span>少</span><i class="level-0"></i><i class="level-1"></i><i class="level-2"></i><i class="level-3"></i><i class="level-4"></i><span>達標</span></div>
      <span>${stats.activeDays} 個飲水日 · ${stats.goalDays} 天達標</span></div>
    <output class="heatmap-detail" id="heatmap-detail" aria-live="polite">點選或觸碰日期查看當日紀錄。</output>`;
}

function renderHeatmapCard(year: number | null, data: HistoryPayload, focusDirection?: number) {
  const card = root.querySelector<HTMLElement>('.heatmap-card');
  if (!card) return;
  heatmapYear = year;
  card.innerHTML = heatmapCardContent(year, data.days);
  card.removeAttribute('aria-busy');
  bindHeatmapInteractions();
  if (focusDirection) {
    card.querySelector<HTMLButtonElement>(`[data-heatmap-direction="${focusDirection}"]`)?.focus();
  }
}

async function loadHeatmap(year: number | null, focusDirection?: number) {
  if (activeView !== 'history' || year === heatmapYear) return;
  const cacheKey = year === null ? 'recent' : String(year);
  const cached = heatmapCache.get(cacheKey);
  if (cached) { renderHeatmapCard(year, cached, focusDirection); return; }
  const generation = viewGeneration;
  const sequence = ++heatmapFetchSequence;
  const card = root.querySelector<HTMLElement>('.heatmap-card');
  card?.setAttribute('aria-busy', 'true');
  card?.querySelectorAll<HTMLButtonElement>('.heatmap-controls button').forEach((button) => { button.disabled = true; });
  const detail = card?.querySelector<HTMLOutputElement>('#heatmap-detail');
  if (detail) detail.textContent = `正在載入${heatmapPeriodLabel(year)}…`;
  try {
    const data = await api<HistoryPayload>(year === null ? '/history?days=366' : `/history?year=${year}`);
    if (activeView !== 'history' || generation !== viewGeneration || sequence !== heatmapFetchSequence) return;
    if (!data.bound) { pairing(); return; }
    heatmapCache.set(cacheKey, data);
    renderHeatmapCard(year, data, focusDirection);
  } catch (error) {
    if (activeView !== 'history' || generation !== viewGeneration || sequence !== heatmapFetchSequence) return;
    card?.removeAttribute('aria-busy');
    card?.querySelectorAll<HTMLButtonElement>('.heatmap-controls button').forEach((button) => { button.disabled = false; });
    if (detail) detail.textContent = `無法載入${heatmapPeriodLabel(year)}，請稍後再試：${(error as Error).message}`;
  }
}

function bindHeatmapInteractions() {
  root.querySelectorAll<HTMLButtonElement>('[data-heatmap-direction]').forEach((button) => {
    button.addEventListener('click', () => {
      const currentYear = Number(heatmapTodayKey.slice(0, 4));
      const direction = Number(button.dataset.heatmapDirection);
      void loadHeatmap(shiftHeatmapYear(heatmapYear, direction, currentYear), direction);
    });
  });
  const grid = root.querySelector<HTMLElement>('.heatmap-grid');
  const detail = root.querySelector<HTMLOutputElement>('#heatmap-detail');
  const scroller = root.querySelector<HTMLElement>('.heatmap-scroll');
  const scrubber = root.querySelector<HTMLInputElement>('#heatmap-scrubber');
  if (!grid || !detail || !scroller || !scrubber) return;
  const cells = Array.from(grid.querySelectorAll<HTMLButtonElement>('.heat-day'));
  const select = (cell: HTMLButtonElement, focus = false) => {
    cells.forEach((item) => {
      item.tabIndex = -1;
      item.classList.toggle('selected', item === cell);
      item.setAttribute('aria-pressed', String(item === cell));
    });
    cell.tabIndex = 0;
    detail.textContent = cell.dataset.detail ?? '';
    scrubber.value = String(Math.max(0, cells.indexOf(cell)));
    if (focus) cell.focus();
  };
  grid.addEventListener('click', (event) => {
    const cell = (event.target as HTMLElement).closest<HTMLButtonElement>('.heat-day');
    if (cell) select(cell);
  });
  grid.addEventListener('keydown', (event) => {
    const cell = (event.target as HTMLElement).closest<HTMLButtonElement>('.heat-day');
    if (!cell) return;
    const index = cells.indexOf(cell);
    const movement = ({ ArrowLeft: -7, ArrowRight: 7, ArrowUp: -1, ArrowDown: 1 } as Record<string, number>)[event.key];
    if (!movement) return;
    event.preventDefault();
    const next = cells[Math.max(0, Math.min(cells.length - 1, index + movement))];
    if (next) select(next, true);
  });
  scrubber.addEventListener('input', () => {
    const cell = cells[Number(scrubber.value)];
    if (!cell) return;
    select(cell);
    scroller.scrollLeft = Math.max(0, cell.offsetLeft - scroller.clientWidth / 2);
  });
  requestAnimationFrame(() => {
    const last = cells[cells.length - 1];
    if (last) select(last);
    scroller.scrollLeft = scroller.scrollWidth;
  });
}

async function renderHistory(generation = viewGeneration) {
  const [data, settingsData] = await Promise.all([
    api<HistoryPayload>('/history?days=366'),
    api<{ bound: boolean; pending: boolean; settings?: Record<string, unknown> }>('/settings')
  ]);
  if (activeView !== 'history' || generation !== viewGeneration) return;
  if (!data.bound) { pairing(); return; }
  const goal = Math.max(1, Math.round(Number(settingsData.settings?.daily_goal_ml) || 2000));
  const todayKey = new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Taipei' });
  const yearKeys = recentDateKeys(todayKey, 365);
  const yearDays = fillHistoryDays(data.days, yearKeys);
  const days = yearDays.slice(-7);
  const currentMonth = todayKey.slice(0, 7);
  const monthDays = yearDays.filter((day) => day.local_date.startsWith(currentMonth));
  const weekStats = historySummary(days, goal);
  const monthStats = historySummary(monthDays, goal);
  const max = Math.max(goal, ...days.map((day) => day.total_ml), 1);
  const enough = weekStats.activeDays >= 7;
  heatmapYear = null;
  heatmapTodayKey = todayKey;
  heatmapGoal = goal;
  heatmapFetchSequence++;
  heatmapCache.clear();
  heatmapCache.set('recent', data);
  shell(`<section class="page-head"><p class="eyebrow">YOUR HYDRATION RHYTHM</p><h1>水的足跡</h1><p>從這個月到一整年，看見自己的飲水節奏，不把健康變成競賽。</p></section>
    <section class="history-summary month-summary" aria-label="本月飲水摘要">
      <div><span>本月總量</span><strong>${formatVolume(monthStats.totalMl)}</strong></div>
      <div><span>有紀錄／達標</span><strong>${monthStats.activeDays}<small>／${monthStats.goalDays} 天</small></strong></div>
      <div><span>飲水日均</span><strong>${formatVolume(monthStats.averageActiveMl)}</strong></div>
      <div><span>最長連續達標</span><strong>${monthStats.longestGoalStreak}<small> 天</small></strong></div>
    </section>
    <section class="history-card week-card"><div class="history-card-head"><div><span class="section-label">LAST SEVEN DAYS</span><h2>最近七天</h2></div><strong>${formatVolume(weekStats.totalMl)}</strong></div>
      <div class="chart-key"><span><i></i>每日飲水</span><span><b></b>目標 ${goal} ml${settingsData.pending ? '（待杯子同步）' : ''}</span></div>
      <div class="bars" style="--goal:${Math.round(goal / max * 100)}%">${days.map((day) => {
      const height = Math.max(4, Math.round(day.total_ml / max * 100));
      const label = dateFromKey(day.local_date).toLocaleDateString('zh-TW', { timeZone: 'Asia/Taipei', weekday: 'short' });
      return `<div class="bar-day${day.total_ml > 0 ? '' : ' is-empty'}" aria-label="${label} ${Math.round(day.total_ml)} ml，共 ${day.drink_count} 次"><span>${Math.round(day.total_ml)}</span><i style="height:${height}%"></i><b>${label}</b></div>`;
    }).join('')}</div></section>
    <section class="history-card heatmap-card">${heatmapCardContent(null, data.days)}</section>
    <section class="insight"><span>${enough ? '七日觀察' : '正在累積'}</span><p>${enough ? `最近七天的飲水日均為 ${formatVolume(weekStats.averageActiveMl)}；保持舒服而可持續的節奏。` : `最近七天再累積 ${Math.max(0, 7 - weekStats.activeDays)} 個飲水日，就能開始看見穩定趨勢。`}</p></section>`);
  bindHeatmapInteractions();
}

async function renderSettings(generation = viewGeneration) {
  const data = await api<SettingsPayload>('/settings');
  if (activeView !== 'settings' || generation !== viewGeneration) return;
  if (!data.bound) { pairing(); return; }
  if (!settingsCommandId && data.pending && data.command?.id) settingsCommandId = data.command.id;
  const settings = data.settings ?? {};
  const stateClass = data.pending ? 'pending' : data.syncStatus === 'rejected' ? 'rejected' : 'synced';
  const stateCopy = data.pending ? '等待杯子 ACK' : data.syncStatus === 'rejected' ? '上次設定遭拒絕' : '已和杯子同步';
  shell(`<section class="page-head"><p class="eyebrow">YOUR RHYTHM</p><h1>照你的方式。</h1><p>設定送出後，等杯子下一次同步才正式生效。</p></section>
    <form class="settings-card" id="settings-form">
      <div class="settings-state ${stateClass}"><i></i><span>${stateCopy}</span></div>
      <div class="setting-row"><div><label for="goal">每日目標</label><p>一個舒服、可持續的數字。</p></div><div class="unit-input"><input id="goal" type="number" min="100" max="9999" value="${Number(settings.daily_goal_ml) || 2000}"><span>ml</span></div></div>
      <div class="setting-row"><div><label for="interval">提醒間隔</label><p>只計算杯子穩定放著的時間。</p></div><div class="unit-input"><input id="interval" type="number" min="1" max="1440" value="${Number(settings.reminder_interval_min) || 60}"><span>分</span></div></div>
      <div class="setting-row"><div><label for="enabled">飲水提醒</label><p>LINE 與實體蜂鳴器使用同一狀態。</p></div><input class="switch" id="enabled" type="checkbox" ${Number(settings.reminder_enabled) ? 'checked' : ''}></div>
      <button class="primary" type="submit" ${data.pending || settingsInFlight ? 'disabled' : ''}>儲存並同步</button>
      <output id="settings-message" aria-live="polite">${data.pending ? '裝置尚未 ACK，設定正在等待同步。' : data.syncStatus === 'rejected' ? '裝置拒絕上次設定，請檢查數值後重試。' : '目前沒有待同步的設定。'}</output>
    </form>
    ${accountControls(true)}`);
  document.querySelector<HTMLFormElement>('#settings-form')!.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (settingsInFlight) return;
    const message = document.querySelector<HTMLOutputElement>('#settings-message')!;
    const submit = (event.currentTarget as HTMLFormElement).querySelector<HTMLButtonElement>('button[type="submit"]')!;
    settingsInFlight = true;
    submit.disabled = true;
    message.textContent = '正在儲存…';
    try {
      const result = await api<{ pending: boolean; command?: { id: string } }>('/settings', { method: 'PATCH', body: JSON.stringify({
        dailyGoalMl: Number(document.querySelector<HTMLInputElement>('#goal')!.value),
        reminderIntervalMin: Number(document.querySelector<HTMLInputElement>('#interval')!.value),
        reminderEnabled: document.querySelector<HTMLInputElement>('#enabled')!.checked
      }) });
      settingsCommandId = result.command?.id ?? null;
      message.textContent = result.pending ? '已儲存，等待 HydraCup ACK。' : '已同步。';
      const state = document.querySelector<HTMLElement>('.settings-state')!;
      state.className = `settings-state ${result.pending ? 'pending' : 'synced'}`;
      state.querySelector('span')!.textContent = result.pending ? '等待杯子 ACK' : '已和杯子同步';
      if (!result.pending) submit.disabled = false;
    } catch (error) {
      message.textContent = `儲存失敗：${(error as Error).message}`;
      submit.disabled = false;
    } finally {
      settingsInFlight = false;
    }
  });
  bindAccountControls(true);
}

async function refreshSettingsSyncState(generation = viewGeneration) {
  if (activeView !== 'settings' || settingsInFlight) return;
  const requestGeneration = ++settingsRefreshGeneration;
  const data = await api<SettingsPayload>('/settings');
  if (settingsInFlight || activeView !== 'settings' || generation !== viewGeneration || requestGeneration !== settingsRefreshGeneration) return;
  if (!data.bound) { pairing(); return; }
  const state = root.querySelector<HTMLElement>('.settings-state');
  const message = root.querySelector<HTMLOutputElement>('#settings-message');
  const submit = root.querySelector<HTMLButtonElement>('#settings-form button[type="submit"]');
  if (!state || !message || !submit) return;
  const rejected = data.syncStatus === 'rejected';
  const otherCommand = Boolean(settingsCommandId && data.command?.id && data.command.id !== settingsCommandId);
  state.className = `settings-state ${data.pending ? 'pending' : rejected ? 'rejected' : 'synced'}`;
  state.querySelector('span')!.textContent = data.pending ? '等待杯子 ACK' : rejected ? '上次設定遭拒絕' : '已和杯子同步';
  message.textContent = otherCommand ? '偵測到另一筆設定操作；目前顯示最新同步狀態。' : data.pending ? '裝置尚未 ACK，設定正在等待同步。' : rejected ? '裝置拒絕上次設定，請檢查數值後重試。' : '杯子已確認最新設定。';
  submit.disabled = data.pending || settingsInFlight;
  settingsCommandId = data.pending ? data.command?.id ?? settingsCommandId : null;
}

async function navigate(view: View) {
  const generation = ++viewGeneration;
  activeView = view;
  history.replaceState(null, '', `#${view}`);
  window.clearInterval(pollTimer);
  if (view === 'history') await renderHistory(generation);
  else if (view === 'settings') {
    await renderSettings(generation);
    if (generation !== viewGeneration || activeView !== view) return;
    pollTimer = window.setInterval(() => refreshSettingsSyncState(generation).catch(() => undefined), 5000);
  }
  else {
    await renderToday(generation);
    if (generation !== viewGeneration || activeView !== view) return;
    pollTimer = window.setInterval(() => renderToday(generation).catch(() => undefined), 5000);
  }
}

async function start() {
  applyTheme(currentTheme());
  shell('<section class="state-page"><div class="loader"></div><p>正在連接你的 HydraCup…</p></section>', { dock: false });
  try {
    if (!await ensureSession()) return;
    const hash = location.hash.slice(1) as View;
    await navigate(['today', 'history', 'settings'].includes(hash) ? hash : 'today');
  } catch (error) {
    shell(`<section class="state-page"><p class="eyebrow">TEMPORARILY OFFLINE</p><h1>先喘口氣。</h1><p>目前無法載入資料：${escapeHtml((error as Error).message)}</p><button class="primary" id="retry">再試一次</button></section>`, { dock: false });
    root.querySelector<HTMLButtonElement>('#retry')?.addEventListener('click', () => location.reload());
  }
}

themeMedia.addEventListener('change', () => {
  if (!localStorage.getItem('hydracup-theme')) applyTheme(themeMedia.matches ? 'dark' : 'light');
});

void start();
